import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { DataRoomNodeReport } from '../../../Reports/types';
import { useTranslation } from 'react-i18next';
import { sentryError } from 'utils';
import { Button, Loader } from 'ncoded-component-library';
import ReportsEmptyState from '../../../Reports/components/ReportsEmptyState';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  BODY_COL_CONTENT,
  COPY_TRANSLATE_MAP,
  FILE_ACTIVITY_COLUMNS,
  isSortName,
  SortName,
  SORT_NAME_MAP,
  POPUP_SORT_NAME,
} from './data';
import { FIRST_PAGE } from 'data/global';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DocumentAnalytics } from 'router/subrouters/DataRoom/components/DocumentAnalytics';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import ManageFileAccess from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';

import './FileActivity.styles.scss';
import './FileActivity.styles.responsive.scss';

const LIMIT = '10';

const checkIsAnalyticsEmpty = ({
  numberOfViews,
  numberOfSecondsSpent,
}: DataRoomNodeReport) => !numberOfViews && !numberOfSecondsSpent;

const checkDoesntHaveAccess = (
  node: DataRoomNode,
  doesntHaveAccess?: boolean,
) => doesntHaveAccess || !node._id;

type FileActivityProps = {
  className?: string;
  withManageAccessCell?: boolean;
  updateNodes?: () => void;
};

const FileActivity: React.FC<FileActivityProps> = (props) => {
  const { className, withManageAccessCell, updateNodes } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<DataRoomNodeReport[]>([]);
  const [sortRecent, setSortRecent] = useState(false);
  const [sort, setSort] = useState(`-${SORT_NAME_MAP.views}`);
  const [selectedAnalytics, setSelectedAnalytics] = useState('');

  const {
    dataRoom: { _id: dataRoomId, name: dataRoomName },
    doesntHaveAccess,
  } = useContext(DataRoomContext);
  const { openNode } = useContext(DocumentViewContext);

  const withManageAccess = !!withManageAccessCell && !doesntHaveAccess;

  const { t } = useTranslation();

  const classes = classNames(
    'file-activity',
    { loading: isLoading },
    className,
  );

  const renderSortButton = useCallback(
    (sortName: SortName, content?: ReactNode) => (
      <SortingButton
        ascDefault={false}
        content={content}
        sortingValue={sortName}
        popupContent={t(POPUP_SORT_NAME[sortName])}
        sort={sort}
        updateSort={setSort}
      />
    ),
    [sort, t],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { items },
        } = await dataRoomApi.reports.getReportByNodeActivity(dataRoomId, {
          $page: FIRST_PAGE,
          $limit: LIMIT,
          $sort: sortRecent ? `-dataRoomNodeCreatedAt ${sort}` : sort,
        });
        setReports(items);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, sort, sortRecent]);

  const drawerRefs = useRef<SideDrawerRef[]>([]);

  return (
    <div className={classes}>
      <div className="header">
        <p>{t('topFiles')}</p>
        <div className="sort-by-recent">
          <Button
            className={classNames({ active: !sortRecent })}
            onClick={() => setSortRecent(false)}
          >
            {t('all')}
          </Button>
          <Button
            className={classNames({ active: sortRecent })}
            onClick={() => setSortRecent(true)}
          >
            {t('recent')}
          </Button>
        </div>
      </div>

      {!reports?.length ? (
        isLoading ? (
          <Loader overlay={false} />
        ) : (
          <ReportsEmptyState />
        )
      ) : (
        <div className="file-activity__scrolling-container">
          <div className="table">
            <div className="thead">
              <div className="tr">
                {FILE_ACTIVITY_COLUMNS.map((col) => (
                  <div className={classNames('th', col)} key={col}>
                    {isSortName(SORT_NAME_MAP[col])
                      ? renderSortButton(
                          SORT_NAME_MAP[col],
                          t(COPY_TRANSLATE_MAP[col]),
                        )
                      : t(COPY_TRANSLATE_MAP[col])}
                  </div>
                ))}
              </div>
            </div>
            <div className="tbody">
              {reports.map((report, i) => {
                const disabled = checkDoesntHaveAccess(
                  report.dataRoomNode,
                  doesntHaveAccess,
                );

                const analyticsDisabled = checkIsAnalyticsEmpty(report);

                return (
                  <React.Fragment key={report._id}>
                    <div className="tr">
                      {FILE_ACTIVITY_COLUMNS.map((col) => {
                        const sortName = SORT_NAME_MAP[col];
                        const isSelected = sortName && sort?.includes(sortName);

                        return BODY_COL_CONTENT[col](report, {
                          isSelected,
                          hasAnalytics: !disabled,
                          withManageAccessCell: withManageAccess,
                          analyticsDisabled,
                          drawerRefOpen: () =>
                            drawerRefs.current[i]?.setOpen(true),
                          openFile: () => {
                            if (disabled) return;
                            openNode(report.dataRoomNode, dataRoomName);
                          },
                          openAnalytics: () => {
                            if (disabled || analyticsDisabled) return;
                            if (selectedAnalytics === report.dataRoomNode._id)
                              setSelectedAnalytics('');
                            else setSelectedAnalytics(report.dataRoomNode._id);
                          },
                          t,
                        });
                      })}

                      <ManageFileAccess
                        ref={(ref) => (drawerRefs.current[i] = ref)}
                        nodes={[report.dataRoomNode]}
                        parentId={report.dataRoomNode?.parentId}
                        updateNodes={updateNodes}
                      />
                    </div>
                    <div className="tr">
                      <div className="td">
                        <DocumentAnalytics
                          node={report.dataRoomNode}
                          isOpen={report.dataRoomNode._id === selectedAnalytics}
                          getDocumentAnalytics={
                            dataRoomApi.nodes.getFileAnalytics
                          }
                        />
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {isLoading && <Loader />}
    </div>
  );
};

export default FileActivity;
